import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Loader2, LogOut, Mail, MapPin, Anchor, Phone, MessageSquare,
  RefreshCw, Download, Users, Inbox, UserPlus, Trash2, KeyRound, X,
  Newspaper, Plus, Pencil, Upload, Eye, EyeOff, DownloadCloud,
} from "lucide-react";
import SEO from "@/components/SEO";
import { articles as staticArticles } from "@/i18n/translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "rota-admin-jwt";
const USER_KEY = "rota-admin-user";

const STATUS_OPTIONS = [
  { value: "new", label: "New", className: "bg-brand/10 text-brand border-brand/30" },
  { value: "contacted", label: "Contacted", className: "bg-amber-100/60 text-amber-800 border-amber-300/60" },
  { value: "closed", label: "Closed", className: "bg-emerald-100/60 text-emerald-800 border-emerald-300/60" },
];

const getStatusMeta = (s) => STATUS_OPTIONS.find((o) => o.value === s) || STATUS_OPTIONS[0];

const authHeader = (t) => ({ Authorization: `Bearer ${t}` });

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [me, setMe] = useState(() => {
    try { return JSON.parse(localStorage.getItem(USER_KEY) || "null"); } catch { return null; }
  });
  const [authed, setAuthed] = useState(false);
  const [bootLoading, setBootLoading] = useState(!!token);
  const [tab, setTab] = useState("inquiries"); // inquiries | users | articles

  // login form
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");

  // inquiries
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ total: 0, items: [] });

  // admin users
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState("");
  const [showAddUser, setShowAddUser] = useState(false);
  const [pwdResetUser, setPwdResetUser] = useState(null);

  // articles
  const [articles, setArticles] = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [articlesError, setArticlesError] = useState("");
  const [editingArticle, setEditingArticle] = useState(null); // null | "new" | article obj

  const handleLogout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken("");
    setMe(null);
    setAuthed(false);
    setData({ total: 0, items: [] });
    setUsers([]);
    setArticles([]);
  }, []);

  const fetchInquiries = useCallback(async (t) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/inquiries?limit=200`, { headers: authHeader(t) });
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 401) handleLogout();
    } finally {
      setLoading(false);
    }
  }, [handleLogout]);

  const fetchUsers = useCallback(async (t) => {
    setUsersLoading(true);
    setUsersError("");
    try {
      const res = await axios.get(`${API}/admin/users`, { headers: authHeader(t) });
      setUsers(res.data.items || []);
    } catch (e) {
      if (e?.response?.status === 401) handleLogout();
      else setUsersError(e?.response?.data?.detail || "Failed to load users");
    } finally {
      setUsersLoading(false);
    }
  }, [handleLogout]);

  // boot: validate stored token via /me
  useEffect(() => {
    const boot = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${API}/admin/auth/me`, { headers: authHeader(token) });
        setMe(res.data);
        localStorage.setItem(USER_KEY, JSON.stringify(res.data));
        setAuthed(true);
        fetchInquiries(token);
      } catch {
        handleLogout();
      } finally {
        setBootLoading(false);
      }
    };
    boot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoginLoading(true);
    try {
      const res = await axios.post(`${API}/admin/auth/login`, {
        email: loginEmail.trim(),
        password: loginPassword,
      });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      localStorage.setItem(USER_KEY, JSON.stringify(res.data.user));
      setToken(res.data.token);
      setMe(res.data.user);
      setAuthed(true);
      setLoginPassword("");
      fetchInquiries(res.data.token);
    } catch (e) {
      setLoginError(e?.response?.data?.detail || "Login failed");
    } finally {
      setLoginLoading(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    setData((prev) => ({
      ...prev,
      items: prev.items.map((it) => (it.id === id ? { ...it, status: newStatus } : it)),
    }));
    try {
      await axios.patch(`${API}/admin/inquiries/${id}`, { status: newStatus }, { headers: authHeader(token) });
    } catch {
      fetchInquiries(token);
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/inquiries/export.csv`, {
        headers: authHeader(token),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
      link.setAttribute("download", `rota-inquiries-${stamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      /* silent */
    }
  };

  const switchToUsers = () => {
    setTab("users");
    if (users.length === 0) fetchUsers(token);
  };

  const fetchArticles = useCallback(async (t) => {
    setArticlesLoading(true);
    setArticlesError("");
    try {
      const res = await axios.get(`${API}/admin/articles`, { headers: authHeader(t) });
      setArticles(res.data.items || []);
    } catch (e) {
      if (e?.response?.status === 401) handleLogout();
      else setArticlesError(e?.response?.data?.detail || "Failed to load articles");
    } finally {
      setArticlesLoading(false);
    }
  }, [handleLogout]);

  const switchToArticles = () => {
    setTab("articles");
    if (articles.length === 0) fetchArticles(token);
  };

  const deleteArticle = async (a) => {
    if (!window.confirm(`Delete article "${a.en?.title || a.slug}"?`)) return;
    try {
      await axios.delete(`${API}/admin/articles/${a.id}`, { headers: authHeader(token) });
      fetchArticles(token);
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed");
    }
  };

  // Flatten static article body ({h,p}[]) into plain-text with `## heading` convention
  const serializeStaticBody = (blocks) => {
    if (!Array.isArray(blocks)) return "";
    return blocks.map((b) => {
      const h = (b?.h || "").trim();
      const p = (b?.p || "").trim();
      if (h && p) return `## ${h}\n${p}`;
      if (h) return `## ${h}`;
      return p;
    }).filter(Boolean).join("\n\n");
  };

  const [importing, setImporting] = useState(false);
  const importStaticArticles = async () => {
    if (!window.confirm(`Import ${staticArticles.length} legacy articles into the panel?`)) return;
    setImporting(true);
    try {
      const payload = {
        articles: staticArticles.map((a) => ({
          slug: a.slug,
          date: a.date,
          read_time: a.readTime || 5,
          status: "published",
          image: "",
          category: {
            en: a.category?.en || "",
            ru: a.category?.ru || "",
            tr: a.category?.tr || "",
          },
          en: {
            title: a.en?.title || "",
            excerpt: a.en?.excerpt || "",
            body: serializeStaticBody(a.en?.body),
          },
          ru: {
            title: a.ru?.title || "",
            excerpt: a.ru?.excerpt || "",
            body: serializeStaticBody(a.ru?.body),
          },
          tr: {
            title: a.tr?.title || "",
            excerpt: a.tr?.excerpt || "",
            body: serializeStaticBody(a.tr?.body),
          },
        })),
      };
      const res = await axios.post(`${API}/admin/articles/bulk-import`, payload, { headers: authHeader(token) });
      alert(`Imported ${res.data.created_count} article(s). Skipped ${res.data.skipped_count} (already existed).`);
      fetchArticles(token);
    } catch (e) {
      alert(e?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  /* --------------------------------- Render --------------------------------- */

  if (bootLoading) {
    return (
      <main className="min-h-screen pt-32 flex items-start justify-center">
        <Loader2 className="animate-spin text-navy/50" size={28} />
      </main>
    );
  }

  if (!authed) {
    return (
      <>
        <SEO path="/admin" title="Rota Yachting · Admin" description="Internal admin panel" />
        <main className="min-h-screen pt-32 pb-24 px-6 sm:px-10 lg:px-16 flex items-start justify-center">
          <div className="w-full max-w-md bg-white border border-navy/10 p-10 shadow-sm" data-testid="admin-login">
            <div className="overline mb-3" style={{ color: "#C9A24A" }}>Internal · Restricted</div>
            <h1 className="font-display text-3xl text-navy">Rota Yachting Admin</h1>
            <p className="mt-2 text-navy/60 text-sm">Sign in with your admin email and password.</p>
            <form onSubmit={handleLogin} className="mt-6 space-y-3">
              <input
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="Email"
                required
                className="w-full bg-bone border border-navy/15 px-4 py-3.5 text-sm text-navy focus:outline-none focus:border-brand"
                data-testid="admin-email-input"
                autoFocus
              />
              <input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                placeholder="Password"
                required
                className="w-full bg-bone border border-navy/15 px-4 py-3.5 text-sm text-navy focus:outline-none focus:border-brand"
                data-testid="admin-password-input"
              />
              {loginError && <div className="text-brand text-sm" data-testid="admin-error">{loginError}</div>}
              <button
                type="submit"
                disabled={loginLoading}
                className="w-full bg-brand text-white py-3.5 text-[11px] tracking-[0.25em] uppercase font-semibold hover:bg-brand-deep transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="admin-login-btn"
              >
                {loginLoading && <Loader2 size={14} className="animate-spin" />}
                Sign In
              </button>
            </form>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <SEO path="/admin" title="Inquiries · Rota Yachting Admin" description="Contact form inquiries" />
      <main className="min-h-screen pt-32 pb-24 px-6 sm:px-10 lg:px-16" data-testid="admin-dashboard">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8 max-w-7xl mx-auto">
          <div>
            <div className="overline mb-2" style={{ color: "#C9A24A" }}>
              Admin · {me?.email}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl text-navy leading-tight">
              {tab === "inquiries" && (<>{data.total} <span className="text-navy/40">contact submissions</span></>)}
              {tab === "users" && (<>Admin <span className="text-navy/40">users</span></>)}
              {tab === "articles" && (<>{articles.length} <span className="text-navy/40">articles</span></>)}
            </h1>
          </div>
          <div className="flex flex-wrap gap-3">
            {tab === "inquiries" && (
              <>
                <button
                  type="button"
                  onClick={downloadCsv}
                  className="flex items-center gap-2 border border-navy/20 px-4 py-2.5 text-[10px] tracking-[0.25em] uppercase text-navy/70 hover:border-brand hover:text-brand"
                  data-testid="admin-export-csv"
                >
                  <Download size={12} /> Export CSV
                </button>
                <button
                  type="button"
                  onClick={() => fetchInquiries(token)}
                  className="flex items-center gap-2 border border-navy/20 px-4 py-2.5 text-[10px] tracking-[0.25em] uppercase text-navy/70 hover:border-brand hover:text-brand"
                  data-testid="admin-refresh"
                >
                  <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
                </button>
              </>
            )}
            {tab === "users" && (
              <button
                type="button"
                onClick={() => setShowAddUser(true)}
                className="flex items-center gap-2 bg-brand text-white px-4 py-2.5 text-[10px] tracking-[0.25em] uppercase hover:bg-brand-deep"
                data-testid="admin-add-user-btn"
              >
                <UserPlus size={12} /> Add Admin
              </button>
            )}
            {tab === "articles" && (
              <>
                <button
                  type="button"
                  onClick={importStaticArticles}
                  disabled={importing}
                  className="flex items-center gap-2 border border-navy/20 px-4 py-2.5 text-[10px] tracking-[0.25em] uppercase text-navy/70 hover:border-brand hover:text-brand disabled:opacity-60"
                  data-testid="admin-import-legacy-btn"
                  title="Import legacy static articles"
                >
                  {importing ? <Loader2 size={12} className="animate-spin" /> : <DownloadCloud size={12} />}
                  Import Legacy
                </button>
                <button
                  type="button"
                  onClick={() => fetchArticles(token)}
                  className="flex items-center gap-2 border border-navy/20 px-4 py-2.5 text-[10px] tracking-[0.25em] uppercase text-navy/70 hover:border-brand hover:text-brand"
                  data-testid="admin-articles-refresh"
                >
                  <RefreshCw size={12} className={articlesLoading ? "animate-spin" : ""} /> Refresh
                </button>
                <button
                  type="button"
                  onClick={() => setEditingArticle("new")}
                  className="flex items-center gap-2 bg-brand text-white px-4 py-2.5 text-[10px] tracking-[0.25em] uppercase hover:bg-brand-deep"
                  data-testid="admin-add-article-btn"
                >
                  <Plus size={12} /> Add Article
                </button>
              </>
            )}
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center gap-2 border border-navy/20 px-4 py-2.5 text-[10px] tracking-[0.25em] uppercase text-navy/70 hover:border-brand hover:text-brand"
              data-testid="admin-logout"
            >
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto mb-6 flex border-b border-navy/10">
          <button
            type="button"
            onClick={() => setTab("inquiries")}
            className={`flex items-center gap-2 px-4 py-3 text-[11px] tracking-[0.25em] uppercase font-semibold transition-colors ${
              tab === "inquiries" ? "text-brand border-b-2 border-brand -mb-px" : "text-navy/55 hover:text-navy"
            }`}
            data-testid="tab-inquiries"
          >
            <Inbox size={13} /> Inquiries
          </button>
          <button
            type="button"
            onClick={switchToUsers}
            className={`flex items-center gap-2 px-4 py-3 text-[11px] tracking-[0.25em] uppercase font-semibold transition-colors ${
              tab === "users" ? "text-brand border-b-2 border-brand -mb-px" : "text-navy/55 hover:text-navy"
            }`}
            data-testid="tab-users"
          >
            <Users size={13} /> Admins
          </button>
          <button
            type="button"
            onClick={switchToArticles}
            className={`flex items-center gap-2 px-4 py-3 text-[11px] tracking-[0.25em] uppercase font-semibold transition-colors ${
              tab === "articles" ? "text-brand border-b-2 border-brand -mb-px" : "text-navy/55 hover:text-navy"
            }`}
            data-testid="tab-articles"
          >
            <Newspaper size={13} /> Insights
          </button>
        </div>

        {/* Inquiries tab */}
        {tab === "inquiries" && (
          <div className="max-w-7xl mx-auto">
            {data.items.length === 0 ? (
              <div className="bg-white border border-navy/10 p-12 text-center text-navy/50">
                {loading ? "Loading…" : "No inquiries yet."}
              </div>
            ) : (
              <ul className="space-y-3">
                {data.items.map((it) => (
                  <li
                    key={it.id}
                    className="bg-white border border-navy/10 p-6 sm:p-7 hover:border-brand/40 transition-colors"
                    data-testid={`inquiry-${it.id}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                      <div>
                        <div className="font-display text-2xl text-navy leading-tight">{it.name}</div>
                        <div className="mt-1 flex flex-wrap gap-3 text-xs text-navy/60">
                          <a href={`mailto:${it.email}`} className="flex items-center gap-1 hover:text-brand">
                            <Mail size={12} /> {it.email}
                          </a>
                          {it.phone && (
                            <a href={`tel:${it.phone}`} className="flex items-center gap-1 hover:text-brand">
                              <Phone size={12} /> {it.phone}
                            </a>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col sm:items-end gap-2">
                        <div className="text-[10px] tracking-[0.2em] uppercase text-navy/45">
                          {new Date(it.created_at).toLocaleString()} · {(it.language || "en").toUpperCase()}
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-block w-2 h-2 rounded-full ${
                              (it.status || "new") === "new"
                                ? "bg-brand"
                                : (it.status || "new") === "contacted"
                                ? "bg-amber-500"
                                : "bg-emerald-500"
                            }`}
                            aria-hidden="true"
                          />
                          <select
                            value={it.status || "new"}
                            onChange={(e) => updateStatus(it.id, e.target.value)}
                            className={`text-[10px] tracking-[0.2em] uppercase font-semibold border px-2.5 py-1.5 cursor-pointer focus:outline-none focus:border-brand ${getStatusMeta(it.status || "new").className}`}
                            data-testid={`status-select-${it.id}`}
                          >
                            {STATUS_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4 text-xs">
                      {it.yacht_name && (
                        <div className="flex items-center gap-1.5 text-navy/70">
                          <Anchor size={12} className="text-brand" /> <span>{it.yacht_name}</span>
                        </div>
                      )}
                      {it.location && (
                        <div className="flex items-center gap-1.5 text-navy/70">
                          <MapPin size={12} className="text-brand" /> <span>{it.location}</span>
                        </div>
                      )}
                      {it.service && (
                        <div className="flex items-center gap-1.5 text-navy/70">
                          <span className="font-semibold text-navy/85">Service:</span> <span>{it.service}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2 items-start text-sm text-navy/85 leading-relaxed">
                      <MessageSquare size={14} className="text-brand mt-1 shrink-0" />
                      <p className="whitespace-pre-line">{it.message}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Users tab */}
        {tab === "users" && (
          <div className="max-w-7xl mx-auto">
            {usersError && (
              <div className="bg-brand/5 border border-brand/30 text-brand text-sm p-4 mb-4" data-testid="admin-users-error">
                {usersError}
              </div>
            )}
            {usersLoading ? (
              <div className="bg-white border border-navy/10 p-12 text-center text-navy/50">Loading…</div>
            ) : users.length === 0 ? (
              <div className="bg-white border border-navy/10 p-12 text-center text-navy/50">No admin users.</div>
            ) : (
              <ul className="bg-white border border-navy/10 divide-y divide-navy/10" data-testid="admin-users-list">
                {users.map((u) => {
                  const isMe = u.id === me?.id;
                  return (
                    <li
                      key={u.id}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-5"
                      data-testid={`admin-user-${u.id}`}
                    >
                      <div>
                        <div className="font-display text-lg text-navy">{u.name || "—"}{isMe && <span className="ml-2 text-[10px] tracking-[0.2em] uppercase text-brand">You</span>}</div>
                        <div className="text-xs text-navy/60 mt-0.5">{u.email}</div>
                        <div className="text-[10px] tracking-[0.2em] uppercase text-navy/40 mt-1">
                          Created {new Date(u.created_at).toLocaleDateString()}
                          <span className="mx-2 text-navy/25">·</span>
                          Last login {u.last_login_at
                            ? new Date(u.last_login_at).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
                            : "—"}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setPwdResetUser(u)}
                          className="flex items-center gap-2 border border-navy/20 px-3 py-2 text-[10px] tracking-[0.25em] uppercase text-navy/70 hover:border-brand hover:text-brand"
                          data-testid={`admin-user-pwd-${u.id}`}
                        >
                          <KeyRound size={12} /> Password
                        </button>
                        <button
                          type="button"
                          disabled={isMe || users.length <= 1}
                          onClick={async () => {
                            if (!window.confirm(`Delete admin ${u.email}?`)) return;
                            try {
                              await axios.delete(`${API}/admin/users/${u.id}`, { headers: authHeader(token) });
                              fetchUsers(token);
                            } catch (e) {
                              alert(e?.response?.data?.detail || "Delete failed");
                            }
                          }}
                          className="flex items-center gap-2 border border-navy/20 px-3 py-2 text-[10px] tracking-[0.25em] uppercase text-navy/70 hover:border-brand hover:text-brand disabled:opacity-40 disabled:cursor-not-allowed"
                          data-testid={`admin-user-delete-${u.id}`}
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {/* Articles tab */}
        {tab === "articles" && (
          <div className="max-w-7xl mx-auto">
            {articlesError && (
              <div className="bg-brand/5 border border-brand/30 text-brand text-sm p-4 mb-4" data-testid="admin-articles-error">
                {articlesError}
              </div>
            )}
            {articlesLoading ? (
              <div className="bg-white border border-navy/10 p-12 text-center text-navy/50">Loading…</div>
            ) : articles.length === 0 ? (
              <div className="bg-white border border-navy/10 p-12 text-center text-navy/50">
                <div>No articles yet. Click "Add Article" to publish your first one.</div>
                {staticArticles.length > 0 && (
                  <button
                    type="button"
                    onClick={importStaticArticles}
                    disabled={importing}
                    className="mt-5 inline-flex items-center gap-2 border border-brand/40 text-brand px-5 py-2.5 text-[10px] tracking-[0.25em] uppercase hover:bg-brand hover:text-white disabled:opacity-60"
                    data-testid="admin-import-legacy-empty-btn"
                  >
                    {importing ? <Loader2 size={12} className="animate-spin" /> : <DownloadCloud size={12} />}
                    Import {staticArticles.length} legacy articles
                  </button>
                )}
              </div>
            ) : (
              <ul className="bg-white border border-navy/10 divide-y divide-navy/10" data-testid="admin-articles-list">
                {articles.map((a) => (
                  <li
                    key={a.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-5"
                    data-testid={`admin-article-${a.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`inline-flex items-center gap-1 text-[9px] tracking-[0.2em] uppercase font-bold px-2 py-0.5 border ${
                            (a.status || "published") === "published"
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : "bg-amber-50 text-amber-700 border-amber-200"
                          }`}
                          data-testid={`admin-article-status-${a.id}`}
                        >
                          {(a.status || "published") === "published" ? <Eye size={10} /> : <EyeOff size={10} />}
                          {a.status || "published"}
                        </span>
                        <span className="font-display text-lg text-navy leading-tight truncate">{a.en?.title || "(no title)"}</span>
                      </div>
                      <div className="mt-1 text-xs text-navy/60 truncate">
                        <span className="text-brand/80">{a.category?.en || "—"}</span>
                        <span className="mx-2 text-navy/25">·</span>
                        {a.date} · {a.read_time} min · /{a.slug}
                      </div>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-navy/40 mt-1">
                        Updated {a.updated_at ? new Date(a.updated_at).toLocaleDateString() : "—"}
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {(a.status || "published") === "published" && (
                        <a
                          href={`/insights/${a.slug}`}
                          target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-2 border border-navy/20 px-3 py-2 text-[10px] tracking-[0.25em] uppercase text-navy/70 hover:border-brand hover:text-brand"
                          data-testid={`admin-article-view-${a.id}`}
                        >
                          View
                        </a>
                      )}
                      <button
                        type="button"
                        onClick={() => setEditingArticle(a)}
                        className="flex items-center gap-2 border border-navy/20 px-3 py-2 text-[10px] tracking-[0.25em] uppercase text-navy/70 hover:border-brand hover:text-brand"
                        data-testid={`admin-article-edit-${a.id}`}
                      >
                        <Pencil size={12} /> Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteArticle(a)}
                        className="flex items-center gap-2 border border-navy/20 px-3 py-2 text-[10px] tracking-[0.25em] uppercase text-navy/70 hover:border-brand hover:text-brand"
                        data-testid={`admin-article-delete-${a.id}`}
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {showAddUser && (
          <AddUserModal
            token={token}
            onClose={() => setShowAddUser(false)}
            onCreated={() => { setShowAddUser(false); fetchUsers(token); }}
          />
        )}
        {pwdResetUser && (
          <PasswordResetModal
            token={token}
            user={pwdResetUser}
            onClose={() => setPwdResetUser(null)}
          />
        )}
        {editingArticle && (
          <ArticleEditorModal
            token={token}
            article={editingArticle === "new" ? null : editingArticle}
            onClose={() => setEditingArticle(null)}
            onSaved={() => { setEditingArticle(null); fetchArticles(token); }}
          />
        )}
      </main>
    </>
  );
};

/* ----------------------------- Modal: Add User ---------------------------- */
const AddUserModal = ({ token, onClose, onCreated }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/users`,
        { name: name.trim(), email: email.trim(), password },
        { headers: authHeader(token) }
      );
      onCreated();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-navy/60 flex items-start justify-center pt-24 px-4" data-testid="add-user-modal">
      <div className="bg-white w-full max-w-md p-8 relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-navy/50 hover:text-navy" data-testid="add-user-close">
          <X size={18} />
        </button>
        <div className="overline mb-2" style={{ color: "#C9A24A" }}>New admin</div>
        <h2 className="font-display text-2xl text-navy mb-5">Create admin user</h2>
        <form onSubmit={submit} className="space-y-3">
          <input
            type="text" required minLength={1}
            value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Full name"
            className="w-full bg-bone border border-navy/15 px-4 py-3 text-sm text-navy focus:outline-none focus:border-brand"
            data-testid="add-user-name"
          />
          <input
            type="email" required
            value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="w-full bg-bone border border-navy/15 px-4 py-3 text-sm text-navy focus:outline-none focus:border-brand"
            data-testid="add-user-email"
          />
          <input
            type="password" required minLength={8}
            value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Password (min 8 chars)"
            className="w-full bg-bone border border-navy/15 px-4 py-3 text-sm text-navy focus:outline-none focus:border-brand"
            data-testid="add-user-password"
          />
          {err && <div className="text-brand text-sm" data-testid="add-user-error">{err}</div>}
          <button
            type="submit" disabled={busy}
            className="w-full bg-brand text-white py-3 text-[11px] tracking-[0.25em] uppercase font-semibold hover:bg-brand-deep disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid="add-user-submit"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Create Admin
          </button>
        </form>
      </div>
    </div>
  );
};

/* -------------------------- Modal: Password Reset ------------------------- */
const PasswordResetModal = ({ token, user, onClose }) => {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/users/${user.id}/password`,
        { password },
        { headers: authHeader(token) }
      );
      setDone(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-navy/60 flex items-start justify-center pt-24 px-4" data-testid="pwd-reset-modal">
      <div className="bg-white w-full max-w-md p-8 relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-navy/50 hover:text-navy" data-testid="pwd-reset-close">
          <X size={18} />
        </button>
        <div className="overline mb-2" style={{ color: "#C9A24A" }}>Password</div>
        <h2 className="font-display text-2xl text-navy">Reset password</h2>
        <p className="text-sm text-navy/60 mt-1 mb-5">For {user.email}</p>
        {done ? (
          <div className="text-emerald-700 text-sm" data-testid="pwd-reset-success">Password updated.</div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <input
              type="password" required minLength={8}
              value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="New password (min 8 chars)"
              className="w-full bg-bone border border-navy/15 px-4 py-3 text-sm text-navy focus:outline-none focus:border-brand"
              data-testid="pwd-reset-input"
              autoFocus
            />
            {err && <div className="text-brand text-sm" data-testid="pwd-reset-error">{err}</div>}
            <button
              type="submit" disabled={busy}
              className="w-full bg-brand text-white py-3 text-[11px] tracking-[0.25em] uppercase font-semibold hover:bg-brand-deep disabled:opacity-60 flex items-center justify-center gap-2"
              data-testid="pwd-reset-submit"
            >
              {busy && <Loader2 size={14} className="animate-spin" />}
              Update Password
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

/* ---------------------------- Modal: Article Editor ---------------------------- */
const BLANK_LANG = { title: "", excerpt: "", body: "" };

const ArticleEditorModal = ({ token, article, onClose, onSaved }) => {
  const isEdit = !!article;
  const [slug, setSlug] = useState(article?.slug || "");
  const [date, setDate] = useState(article?.date || new Date().toISOString().slice(0, 10));
  const [readTime, setReadTime] = useState(article?.read_time || 5);
  const [image, setImage] = useState(article?.image || "");
  const [status, setStatus] = useState(article?.status || "published");
  const [catEn, setCatEn] = useState(article?.category?.en || "");
  const [catRu, setCatRu] = useState(article?.category?.ru || "");
  const [catTr, setCatTr] = useState(article?.category?.tr || "");
  const [en, setEn] = useState(article?.en ? { title: article.en.title || "", excerpt: article.en.excerpt || "", body: article.en.body || "" } : { ...BLANK_LANG });
  const [ru, setRu] = useState(article?.ru ? { title: article.ru.title || "", excerpt: article.ru.excerpt || "", body: article.ru.body || "" } : { ...BLANK_LANG });
  const [tr, setTr] = useState(article?.tr ? { title: article.tr.title || "", excerpt: article.tr.excerpt || "", body: article.tr.body || "" } : { ...BLANK_LANG });
  const [langTab, setLangTab] = useState("en");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (file) => {
    if (!file) return;
    setErr("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/uploads/image`, fd, {
        headers: { ...authHeader(token), "Content-Type": "multipart/form-data" },
      });
      setImage(res.data.url);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!en.title.trim()) { setErr("English title is required."); return; }
    setBusy(true);
    try {
      const payload = {
        slug: slug.trim() || null,
        date,
        read_time: Number(readTime) || 5,
        image: image.trim() || null,
        status,
        category: { en: catEn.trim(), ru: catRu.trim(), tr: catTr.trim() },
        en: { title: en.title.trim(), excerpt: en.excerpt, body: en.body },
        ru: { title: ru.title.trim() || en.title.trim(), excerpt: ru.excerpt, body: ru.body },
        tr: { title: tr.title.trim() || en.title.trim(), excerpt: tr.excerpt, body: tr.body },
      };
      if (isEdit) {
        await axios.patch(`${API}/admin/articles/${article.id}`, payload, { headers: authHeader(token) });
      } else {
        await axios.post(`${API}/admin/articles`, payload, { headers: authHeader(token) });
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const current = langTab === "en" ? en : langTab === "ru" ? ru : tr;
  const setCurrent = langTab === "en" ? setEn : langTab === "ru" ? setRu : setTr;

  return (
    <div className="fixed inset-0 z-50 bg-navy/60 flex items-start justify-center pt-10 px-4 overflow-y-auto pb-10" data-testid="article-editor-modal">
      <div className="bg-white w-full max-w-3xl p-8 relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-navy/50 hover:text-navy" data-testid="article-editor-close">
          <X size={18} />
        </button>
        <div className="overline mb-2" style={{ color: "#C9A24A" }}>{isEdit ? "Edit" : "New"} · Insights</div>
        <h2 className="font-display text-2xl text-navy mb-6">{isEdit ? "Edit article" : "Create article"}</h2>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-[10px] tracking-[0.2em] uppercase text-navy/50 mb-1">Date</label>
              <input type="date" required value={date} onChange={(e) => setDate(e.target.value)}
                className="w-full bg-bone border border-navy/15 px-3 py-2.5 text-sm text-navy focus:outline-none focus:border-brand"
                data-testid="article-date" />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.2em] uppercase text-navy/50 mb-1">Read time (min)</label>
              <input type="number" min="1" max="60" required value={readTime} onChange={(e) => setReadTime(e.target.value)}
                className="w-full bg-bone border border-navy/15 px-3 py-2.5 text-sm text-navy focus:outline-none focus:border-brand"
                data-testid="article-read-time" />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.2em] uppercase text-navy/50 mb-1">Slug (optional)</label>
              <input type="text" value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="auto from title"
                className="w-full bg-bone border border-navy/15 px-3 py-2.5 text-sm text-navy focus:outline-none focus:border-brand"
                data-testid="article-slug" />
            </div>
          </div>

          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase text-navy/50 mb-1">Hero image</label>
            <div className="flex gap-2">
              <input type="text" value={image} onChange={(e) => setImage(e.target.value)} placeholder="/api/uploads/... or /assets/... or https://..."
                className="flex-1 bg-bone border border-navy/15 px-3 py-2.5 text-sm text-navy focus:outline-none focus:border-brand"
                data-testid="article-image" />
              <label
                className="flex items-center gap-2 border border-navy/20 px-3 py-2.5 text-[10px] tracking-[0.25em] uppercase text-navy/70 hover:border-brand hover:text-brand cursor-pointer"
                data-testid="article-image-upload-label"
              >
                {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                {uploading ? "Uploading…" : "Upload"}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  onChange={(e) => { if (e.target.files?.[0]) handleImageUpload(e.target.files[0]); e.target.value = ""; }}
                  className="hidden"
                  disabled={uploading}
                  data-testid="article-image-upload-input"
                />
              </label>
            </div>
            {image && (
              <div className="mt-2 flex items-center gap-3">
                <img src={image} alt="Hero preview" className="w-32 h-20 object-cover border border-navy/15" />
                <button
                  type="button"
                  onClick={() => setImage("")}
                  className="text-[10px] tracking-[0.25em] uppercase text-navy/50 hover:text-brand"
                  data-testid="article-image-clear"
                >
                  Remove
                </button>
              </div>
            )}
          </div>

          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase text-navy/50 mb-1">Status</label>
            <div className="flex gap-2">
              {[
                { value: "published", label: "Published", Icon: Eye },
                { value: "draft", label: "Draft", Icon: EyeOff },
              ].map(({ value, label, Icon }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setStatus(value)}
                  className={`flex items-center gap-2 px-4 py-2 text-[10px] tracking-[0.25em] uppercase border ${
                    status === value
                      ? value === "published"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                        : "bg-amber-50 text-amber-700 border-amber-300"
                      : "border-navy/15 text-navy/50 hover:border-brand hover:text-brand"
                  }`}
                  data-testid={`article-status-${value}`}
                >
                  <Icon size={12} /> {label}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-navy/45 mt-1">Drafts are hidden from the public Insights page.</p>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[10px] tracking-[0.2em] uppercase text-navy/50 mb-1">Category · EN</label>
              <input type="text" value={catEn} onChange={(e) => setCatEn(e.target.value)} placeholder="Operations"
                className="w-full bg-bone border border-navy/15 px-3 py-2 text-sm text-navy focus:outline-none focus:border-brand"
                data-testid="article-cat-en" />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.2em] uppercase text-navy/50 mb-1">Category · RU</label>
              <input type="text" value={catRu} onChange={(e) => setCatRu(e.target.value)} placeholder="Операции"
                className="w-full bg-bone border border-navy/15 px-3 py-2 text-sm text-navy focus:outline-none focus:border-brand"
                data-testid="article-cat-ru" />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.2em] uppercase text-navy/50 mb-1">Category · TR</label>
              <input type="text" value={catTr} onChange={(e) => setCatTr(e.target.value)} placeholder="Operasyon"
                className="w-full bg-bone border border-navy/15 px-3 py-2 text-sm text-navy focus:outline-none focus:border-brand"
                data-testid="article-cat-tr" />
            </div>
          </div>

          {/* Language tabs */}
          <div className="border border-navy/15 bg-bone/40">
            <div className="flex border-b border-navy/15">
              {["en", "ru", "tr"].map((code) => (
                <button
                  key={code} type="button" onClick={() => setLangTab(code)}
                  className={`flex-1 px-4 py-2.5 text-[11px] tracking-[0.25em] uppercase font-semibold ${
                    langTab === code ? "bg-white text-brand border-b-2 border-brand -mb-px" : "text-navy/55 hover:text-navy"
                  }`}
                  data-testid={`article-lang-${code}`}
                >
                  {code}
                </button>
              ))}
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-[10px] tracking-[0.2em] uppercase text-navy/50 mb-1">
                  Title {langTab === "en" && <span className="text-brand">*</span>}
                </label>
                <input type="text" value={current.title}
                  onChange={(e) => setCurrent({ ...current, title: e.target.value })}
                  className="w-full bg-white border border-navy/15 px-3 py-2.5 text-sm text-navy focus:outline-none focus:border-brand"
                  data-testid={`article-${langTab}-title`} />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.2em] uppercase text-navy/50 mb-1">Excerpt</label>
                <textarea rows={3} value={current.excerpt}
                  onChange={(e) => setCurrent({ ...current, excerpt: e.target.value })}
                  className="w-full bg-white border border-navy/15 px-3 py-2.5 text-sm text-navy focus:outline-none focus:border-brand"
                  data-testid={`article-${langTab}-excerpt`} />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.2em] uppercase text-navy/50 mb-1">
                  Body (plain text — blank line = new paragraph, `## ` at line start = heading)
                </label>
                <textarea rows={12} value={current.body}
                  onChange={(e) => setCurrent({ ...current, body: e.target.value })}
                  className="w-full bg-white border border-navy/15 px-3 py-2.5 text-sm text-navy leading-relaxed focus:outline-none focus:border-brand font-mono"
                  data-testid={`article-${langTab}-body`} />
              </div>
            </div>
          </div>

          {err && <div className="text-brand text-sm" data-testid="article-editor-error">{err}</div>}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 border border-navy/20 py-3 text-[11px] tracking-[0.25em] uppercase text-navy/70 hover:border-brand hover:text-brand">
              Cancel
            </button>
            <button type="submit" disabled={busy}
              className="flex-[2] bg-brand text-white py-3 text-[11px] tracking-[0.25em] uppercase font-semibold hover:bg-brand-deep disabled:opacity-60 flex items-center justify-center gap-2"
              data-testid="article-editor-submit">
              {busy && <Loader2 size={14} className="animate-spin" />}
              {isEdit ? "Save changes" : "Publish article"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Admin;
